import { useEffect } from "react";
import { Btn } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { formatUsdPerLb, periodLabel } from "./callForm";
import { monthName, recordedLabel } from "./format";
import { KIND_LABEL } from "./logTabs";
import type { CallDetail, RefCode } from "./types";

interface Props {
  call: CallDetail;
  landedPriceUseOptions: RefCode[];
  canVoid: boolean;
  onVoid: () => void;
  onClose: () => void;
}

export function CallDetailDialog({ call, landedPriceUseOptions, canVoid, onVoid, onClose }: Props) {
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const callMonthName = monthName(call.callMonth.slice(0, 7));
  const landedPriceUseLabel =
    landedPriceUseOptions.find((option) => option.code === call.landedPriceUseCode)?.displayName ??
    call.landedPriceUseCode;
  const evidenceCount = call.evidence.length;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-[rgba(12,14,10,0.65)] z-1000 p-4"
      role="presentation"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        role="dialog"
        aria-modal="true"
        aria-label={`${callMonthName} call`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-2">
          <h3 className="m-0 text-base">{callMonthName} · Call</h3>
          {call.status === "Voided" && <Chip variant="bad">Voided</Chip>}
        </div>
        <p className="m-0 mb-4 text-rs-light-gray text-[13px]">
          {call.enteredByDisplayName} · saved {recordedLabel(call.recordedUtc)}
        </p>

        <div className="grid gap-3 md:grid-cols-2">
          {call.periods.map((period) => (
            <article key={period.periodOffset} className="border border-rs-jet rounded-md p-3.5">
              <Chip variant={period.periodOffset === 0 ? "ok" : "info"}>
                {periodLabel(period.periodOffset)} · {monthName(period.periodMonth.slice(0, 7))}
              </Chip>
              <p className="mt-2.5 mb-2 text-white text-lg">{formatUsdPerLb(period.fmpUsdPerLb)}</p>
              <p className="m-0 mb-1.5 text-[13px] text-rs-off-white whitespace-pre-wrap">
                <b>Why this price:</b> {period.rationale}
              </p>
              <p className="m-0 text-[13px] text-rs-off-white whitespace-pre-wrap">
                <b>Revisit when:</b> {period.revisitTrigger}
              </p>
            </article>
          ))}
        </div>

        <p className="mt-4 mb-0 text-[13px] text-rs-light-gray">
          Landed-price comparison: <span className="text-rs-off-white">{landedPriceUseLabel}</span>
        </p>

        <details className="mt-4">
          <summary className="cursor-pointer text-[13.5px] text-white">
            Evidence at this call · {evidenceCount} {evidenceCount === 1 ? "record" : "records"}
          </summary>
          {evidenceCount === 0 ? (
            <p className="mt-3 text-rs-light-gray text-[13px]">
              Nothing was logged for this month when the call was saved.
            </p>
          ) : (
            <ul className="mt-3 flex flex-col gap-1.5 list-none p-0 m-0">
              {call.evidence.map((logEntry) => (
                <li
                  key={`${logEntry.kind}-${logEntry.id}`}
                  className="grid grid-cols-[130px_1fr_auto] items-center gap-4 border border-rs-jet rounded-md px-3.5 py-2"
                >
                  <Chip variant={logEntry.kind === "Note" ? "default" : "info"}>
                    {logEntry.kind ? KIND_LABEL[logEntry.kind] : "Record"}
                  </Chip>
                  <span className="text-rs-off-white text-[13px] truncate">{logEntry.summary}</span>
                  <span className="text-rs-light-gray text-xs whitespace-nowrap">
                    {recordedLabel(logEntry.recordedUtc)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </details>

        <div className="flex justify-end gap-2 mt-5">
          {canVoid && call.status === "Active" && (
            <Btn variant="ghost" onClick={onVoid}>
              <span className="material-symbols-sharp">block</span>Void
            </Btn>
          )}
          <Btn variant="ghost" onClick={onClose}>
            Close
          </Btn>
        </div>
      </Card>
    </div>
  );
}
